import {
  BILLING_PHONE_RULES,
  formatBillingPhoneInput,
  resolveBillingCountryCode,
} from './billingPhone';

/**
 * @typedef {import('./orderInvoiceBilling').OrderInvoiceBilling} OrderInvoiceBilling
 */

/**
 * @param {string} rawPhone
 * @param {string} countryCode
 * @returns {string}
 */
function mapProfilePhoneToBillingPhone(rawPhone, countryCode) {
  const trimmedPhone = String(rawPhone || '').trim();
  if (!trimmedPhone) {
    return '';
  }

  const rule = BILLING_PHONE_RULES[countryCode];
  const digits = trimmedPhone.replace(/\D/g, '');

  if (trimmedPhone.startsWith('+')) {
    return formatBillingPhoneInput(trimmedPhone, countryCode);
  }

  if (!digits) {
    return '';
  }

  const hasCallingCodePrefix =
    digits.startsWith(rule.callingCode) && digits.length > rule.maxNationalLength;

  const normalizedDigits = hasCallingCodePrefix
    ? digits
    : `${rule.callingCode}${digits}`;

  return formatBillingPhoneInput(`+${normalizedDigits}`, countryCode);
}

/**
 * Maps authenticated user profile fields to editable checkout billing defaults.
 * Missing registration fields (city, postal code, etc.) are left empty.
 *
 * @param {Record<string, string | undefined> | null | undefined} profile
 * @returns {Partial<OrderInvoiceBilling>}
 */
export function mapUserProfileToBilling(profile) {
  if (!profile || typeof profile !== 'object') {
    return {};
  }

  const name = [profile.nombre, profile.primerApellido, profile.segundoApellido]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');

  const country = resolveBillingCountryCode(
    profile.country || profile.pais || profile.billingCountry,
  );

  return {
    name,
    email: String(profile.email || '').trim(),
    phone: mapProfilePhoneToBillingPhone(profile.telefono || profile.phone, country),
    address: String(profile.direccion || profile.address || '').trim(),
    city: String(profile.ciudad || profile.city || '').trim(),
    postalCode: String(
      profile.codigoPostal || profile.postalCode || profile.cp || '',
    ).trim(),
    country,
  };
}
